import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTheme } from "@/components/ThemeProvider";

// Get relative luminance for a color
function getLuminanceFromHsl(hsl: string): number {
  const parts = hsl.split(" ");
  if (parts.length !== 3) return 0;
  
  const h = parseFloat(parts[0]) || 0;
  const s = parseFloat(parts[1]) / 100 || 0;
  const l = parseFloat(parts[2]) / 100 || 0;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  const toSRGB = (val: number) => {
    const v = val + m;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * toSRGB(r) + 0.7152 * toSRGB(g) + 0.0722 * toSRGB(b);
}

// Get accessible text color (black or white) for a background
function getAccessibleTextColor(backgroundHsl: string): string {
  const luminance = getLuminanceFromHsl(backgroundHsl);
  // Use white text on dark backgrounds (luminance < 0.179), black on light
  return luminance > 0.179 ? "0 0% 10%" : "0 0% 98%";
}

/**
 * Applies dynamic theme colors and fonts from site settings to CSS variables.
 * Place this component once in your app (e.g., in App.tsx or Layout).
 */
export function DynamicTheme() {
  const { themeSettings, isLoading } = useSiteSettings();
  const { setDefaultFromSettings } = useTheme();

  // Sync default theme from settings
  useEffect(() => {
    if (isLoading) return;
    
    const showToggle = themeSettings.lookAndFeel?.showThemeToggle !== false;
    const defaultTheme = themeSettings.lookAndFeel?.defaultTheme || "system";
    setDefaultFromSettings(defaultTheme, showToggle);
  }, [isLoading, themeSettings.lookAndFeel?.showThemeToggle, themeSettings.lookAndFeel?.defaultTheme, setDefaultFromSettings]);

  useEffect(() => {
    if (isLoading) return;

    // Load Google Fonts dynamically
    const fonts = themeSettings.fonts || { heading: "Inter", body: "Inter" };
    const fontFamilies = [...new Set([fonts.heading, fonts.body])];
    
    // Remove old font link if exists
    const oldFontLink = document.getElementById("dynamic-fonts");
    if (oldFontLink) oldFontLink.remove();
    
    // Inter is served from this site (see index.css), so only other families need Google Fonts.
    const extraFamilies = fontFamilies.filter((f) => f && f !== "Inter");
    if (extraFamilies.length > 0) {
      const fontLink = document.createElement("link");
      fontLink.id = "dynamic-fonts";
      fontLink.rel = "stylesheet";
      fontLink.href = `https://fonts.googleapis.com/css2?${extraFamilies.map(f => `family=${f.replace(/ /g, "+")}:wght@400;500;600;700`).join("&")}&display=swap`;
      document.head.appendChild(fontLink);
    }

    // Update favicon dynamically - only if it's a valid URL
    // We wait until settings are loaded to avoid showing default favicon first
    const faviconUrl = themeSettings.favicon?.trim();
    if (faviconUrl && (faviconUrl.startsWith('/') || faviconUrl.startsWith('http'))) {
      let faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
      if (!faviconLink) {
        faviconLink = document.createElement("link");
        faviconLink.rel = "icon";
        document.head.appendChild(faviconLink);
      }
      // Only update if different to avoid re-triggering load
      if (faviconLink.href !== faviconUrl) {
        faviconLink.href = faviconUrl;
      }
      
      // Also update apple-touch-icon if it exists
      let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null;
      if (!appleTouchIcon) {
        appleTouchIcon = document.createElement("link");
        appleTouchIcon.rel = "apple-touch-icon";
        document.head.appendChild(appleTouchIcon);
      }
      if (appleTouchIcon.href !== faviconUrl) {
        appleTouchIcon.href = faviconUrl;
      }
    }

    // Use a style tag (not inline styles) so `.dark { ... }` can properly override
    // and the ThemeToggle keeps working.
    let styleEl = document.getElementById("dynamic-theme") as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "dynamic-theme";
      document.head.appendChild(styleEl);
    }

    const light = themeSettings.light;
    const dark = themeSettings.dark;
    const lookAndFeel = themeSettings.lookAndFeel || {
      borderRadius: "rounded",
      cardRadius: "rounded",
      shadowIntensity: "medium",
      animationSpeed: "smooth",
      buttonStyle: "flat",
      cardBorderStyle: "subtle",
      density: "comfortable",
      buttonTypography: "bold-uppercase",
      buttonHoverEffect: "lift",
    };

    // Map look and feel values to CSS
    const borderRadiusButtonMap: Record<string, string> = {
      none: "0",
      subtle: "0.25rem",
      rounded: "0.5rem",
      pill: "9999px",
    };
    
    // Cards have their own radius setting
    const borderRadiusCardMap: Record<string, string> = {
      none: "0",
      subtle: "0.25rem",
      rounded: "0.5rem",
      "extra-rounded": "1rem",
    };
    
    const shadowMap: Record<string, string> = {
      none: "none",
      subtle: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
      medium: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
      dramatic: "0 20px 25px -5px rgb(0 0 0 / 0.15), 0 8px 10px -6px rgb(0 0 0 / 0.15)",
    };
    
    const animationSpeedMap: Record<string, string> = {
      none: "0ms",
      snappy: "150ms",
      smooth: "300ms",
      slow: "500ms",
    };

    const densityMap: Record<string, { base: string; sm: string; md: string; lg: string }> = {
      compact: { base: "0.25rem", sm: "0.5rem", md: "0.75rem", lg: "1rem" },
      comfortable: { base: "0.5rem", sm: "0.75rem", md: "1rem", lg: "1.5rem" },
      spacious: { base: "0.75rem", sm: "1rem", md: "1.5rem", lg: "2rem" },
    };

    const buttonTypographyMap: Record<string, string> = {
      normal: "font-size: 0.875rem; font-weight: 500; text-transform: none; letter-spacing: 0;",
      bold: "font-size: 0.875rem; font-weight: 700; text-transform: none; letter-spacing: 0;",
      uppercase: "font-size: 0.875rem; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em;",
      "bold-uppercase": "font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;",
    };

    const buttonHoverEffectMap: Record<string, string> = {
      none: "",
      lift: "transform: translateY(-4px); box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);",
      scale: "transform: scale(1.08);",
      glow: "box-shadow: 0 0 30px hsl(var(--primary) / 0.6), 0 0 60px hsl(var(--primary) / 0.3);",
      "lift-scale": "transform: translateY(-4px) scale(1.05); box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);",
      pulse: "animation: btn-pulse 0.6s ease-in-out;",
      "border-glow": "box-shadow: inset 0 0 0 2px hsl(var(--primary)), 0 0 20px hsl(var(--primary) / 0.4);",
      shake: "animation: btn-shake 0.4s ease-in-out;",
      bounce: "animation: btn-bounce 0.4s ease-in-out;",
    };

    const navLinkHoverEffect = lookAndFeel.navLinkHoverEffect || "underline";

    const buttonRadius = borderRadiusButtonMap[lookAndFeel.borderRadius] || "0.5rem";
    const cardRadius = borderRadiusCardMap[lookAndFeel.cardRadius] || "0.5rem";
    const shadow = shadowMap[lookAndFeel.shadowIntensity] || shadowMap.medium;
    const duration = animationSpeedMap[lookAndFeel.animationSpeed] || "300ms";
    const buttonStyle = lookAndFeel.buttonStyle || "flat";
    const cardBorderStyle = lookAndFeel.cardBorderStyle || "subtle";
    const density = densityMap[lookAndFeel.density] || densityMap.comfortable;
    const buttonTypography = buttonTypographyMap[lookAndFeel.buttonTypography] || buttonTypographyMap["bold-uppercase"];
    const buttonHoverEffect = buttonHoverEffectMap[lookAndFeel.buttonHoverEffect] || buttonHoverEffectMap.lift;

    // Generate button style CSS with accessible text color
    const getButtonStyles = (primary: string, scopeSelector?: string) => {
      const textColor = getAccessibleTextColor(primary);
      const scope = scopeSelector ? `${scopeSelector} ` : "";

      switch (buttonStyle) {
        case "outlined":
          return `
            ${scope}.btn-primary-custom {
              background: transparent;
              border: 2px solid hsl(${primary});
              color: hsl(${primary});
            }
            ${scope}.btn-primary-custom:hover {
              background: hsl(${primary} / 0.1);
            }
          `;
        case "gradient":
          return `
            ${scope}.btn-primary-custom {
              background: linear-gradient(135deg, hsl(${primary}), hsl(${primary} / 0.8));
              border: none;
              color: hsl(${textColor});
              box-shadow: 0 4px 14px 0 hsl(${primary} / 0.4);
            }
            ${scope}.btn-primary-custom:hover {
              box-shadow: 0 6px 20px 0 hsl(${primary} / 0.5);
              transform: translateY(-1px);
            }
          `;
        case "soft":
          return `
            ${scope}.btn-primary-custom {
              background: hsl(${primary} / 0.15);
              border: none;
              color: hsl(${primary});
            }
            ${scope}.btn-primary-custom:hover {
              background: hsl(${primary} / 0.25);
            }
          `;
        default: // flat
          return `
            ${scope}.btn-primary-custom {
              background: hsl(${primary});
              border: none;
              color: hsl(${textColor});
            }
            ${scope}.btn-primary-custom:hover {
              background: hsl(${primary} / 0.9);
            }
          `;
      }
    };

    // Generate card border CSS
    const getCardBorderStyles = (primary: string, scopeSelector?: string) => {
      const scope = scopeSelector ? `${scopeSelector} ` : "";

      switch (cardBorderStyle) {
        case "none":
          return `
            ${scope}.card-custom {
              border: none;
            }
          `;
        case "solid":
          return `
            ${scope}.card-custom {
              border: 2px solid hsl(var(--border));
            }
          `;
        case "accent":
          return `
            ${scope}.card-custom {
              border: 2px solid hsl(${primary});
            }
          `;
        default: // subtle
          return `
            ${scope}.card-custom {
              border: 1px solid hsl(var(--border));
            }
          `;
      }
    };

    // Calculate accessible text colors for primary buttons
    const lightPrimaryForeground = getAccessibleTextColor(light.primary);
    const darkPrimaryForeground = getAccessibleTextColor(dark.primary);
    const lightAccentForeground = getAccessibleTextColor(light.accent);
    const darkAccentForeground = getAccessibleTextColor(dark.accent);

    styleEl.textContent = `
      :root {
        --primary: ${light.primary};
        --primary-foreground: ${lightPrimaryForeground};
        --accent: ${light.accent};
        --accent-foreground: ${lightAccentForeground};
        --background: ${light.background};
        --foreground: ${light.foreground};
        --card: ${light.card};
        --card-foreground: ${light.foreground};
        --muted: ${light.muted};
        --muted-foreground: ${light.foreground};
        --ring: ${light.primary};

        --sidebar-primary: ${light.primary};
        --sidebar-ring: ${light.primary};

        --font-heading: "${fonts.heading}", sans-serif;
        --font-body: "${fonts.body}", sans-serif;

        /* Base radius (used by most UI surfaces) */
        --radius: ${cardRadius};
        /* Card radius (used by Card component explicitly) */
        --radius-card: ${cardRadius};
        /* Control radius (used by buttons/inputs/selects explicitly) */
        --radius-button: ${buttonRadius};

        --shadow: ${shadow};
        --transition-duration: ${duration};
        --button-style: ${buttonStyle};
        --card-border-style: ${cardBorderStyle};

        --spacing-base: ${density.base};
        --spacing-sm: ${density.sm};
        --spacing-md: ${density.md};
        --spacing-lg: ${density.lg};
      }

      .dark {
        --primary: ${dark.primary};
        --primary-foreground: ${darkPrimaryForeground};
        --accent: ${dark.accent};
        --accent-foreground: ${darkAccentForeground};
        --background: ${dark.background};
        --foreground: ${dark.foreground};
        --card: ${dark.card};
        --card-foreground: ${dark.foreground};
        --muted: ${dark.muted};
        --muted-foreground: ${dark.foreground};
        --ring: ${dark.primary};

        --sidebar-primary: ${dark.primary};
        --sidebar-ring: ${dark.primary};
      }

      h1, h2, h3, h4, h5, h6 {
        font-family: var(--font-heading);
      }

      body {
        font-family: var(--font-body);
      }

      /* Apply transition duration to interactive elements */
      button, a, input, select, textarea, .transition-all, .transition-colors {
        transition-duration: var(--transition-duration) !important;
      }

      /* Button style variations */
      ${getButtonStyles(light.primary)}
      ${getButtonStyles(dark.primary, ".dark")}

      /* Card border style variations */
      ${getCardBorderStyles(light.primary)}
      ${getCardBorderStyles(dark.primary, ".dark")}

      /* Button typography */
      .btn-typography {
        ${buttonTypography}
      }

      /* Button hover effects - only apply to buttons without nav-link-hover class */
      .btn-hover:not(.nav-link-hover):hover:not(:disabled) {
        ${buttonHoverEffect}
      }
      /* When nav-link-hover is set to "none", btn-hover should apply */
      ${navLinkHoverEffect === "none" ? `
        .btn-hover.nav-link-hover:hover:not(:disabled) {
          ${buttonHoverEffect}
        }
      ` : ""}

      /* Nav link hover effects */
      .nav-link-hover {
        position: relative;
        transition: all var(--transition-duration) ease;
         text-decoration: none;
         outline: none;
         -webkit-tap-highlight-color: transparent;
      }

       /* Prevent default mouse-focus outline/ink that can look like a black bar on mousedown.
          Keep keyboard accessibility via :focus-visible behavior elsewhere. */
       .nav-link-hover:focus:not(:focus-visible) {
         outline: none;
         box-shadow: none;
       }

       .nav-link-hover:active {
         outline: none;
         text-decoration: none;
         box-shadow: none;
       }
      /* Button hover keyframes */
      @keyframes btn-pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.06); }
      }
      @keyframes btn-shake {
        0%, 100% { transform: translateX(0); }
        20%, 60% { transform: translateX(-3px); }
        40%, 80% { transform: translateX(3px); }
      }
      @keyframes btn-bounce {
        0%, 100% { transform: translateY(0); }
        40% { transform: translateY(-6px); }
        60% { transform: translateY(-3px); }
      }

      /* Nav link keyframes */
      @keyframes nav-slide-in {
        from { width: 0; }
        to { width: 80%; }
      }
      @keyframes nav-dot-appear {
        from { transform: translateX(-50%) scale(0); }
        to { transform: translateX(-50%) scale(1); }
      }

      ${navLinkHoverEffect === "underline" ? `
        .nav-link-hover::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 2px;
          background: hsl(var(--primary));
          transition: all var(--transition-duration) ease;
          transform: translateX(-50%);
        }
        .nav-link-hover:hover,
        .nav-link-hover:active {
          transform: none;
          box-shadow: none;
        }
        .nav-link-hover:hover::after,
        .nav-link-hover:active::after {
          width: 80%;
        }
      ` : ""}
      ${navLinkHoverEffect === "scale" ? `
        .nav-link-hover:hover,
        .nav-link-hover:active {
          transform: scale(1.05);
          box-shadow: none;
        }
      ` : ""}
      ${navLinkHoverEffect === "fade" ? `
        .nav-link-hover {
          opacity: 0.7;
        }
        .nav-link-hover:hover,
        .nav-link-hover:active {
          opacity: 1;
          transform: none;
          box-shadow: none;
        }
      ` : ""}
      ${navLinkHoverEffect === "highlight" ? `
        .nav-link-hover:hover,
        .nav-link-hover:active {
          background: hsl(var(--primary) / 0.15);
          color: hsl(var(--primary));
          transform: none;
          box-shadow: none;
        }
      ` : ""}
      ${navLinkHoverEffect === "slide-bg" ? `
        .nav-link-hover::before {
          content: '';
          position: absolute;
          inset: 0;
          background: hsl(var(--primary) / 0.1);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform var(--transition-duration) ease;
          border-radius: inherit;
          z-index: -1;
        }
        .nav-link-hover:hover::before,
        .nav-link-hover:active::before {
          transform: scaleX(1);
        }
        .nav-link-hover:hover,
        .nav-link-hover:active {
          color: hsl(var(--primary));
          transform: none;
          box-shadow: none;
        }
      ` : ""}
      ${navLinkHoverEffect === "dot" ? `
        .nav-link-hover::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: hsl(var(--primary));
          transform: translateX(-50%) scale(0);
          transition: transform var(--transition-duration) ease;
        }
        .nav-link-hover:hover::after,
        .nav-link-hover:active::after {
          transform: translateX(-50%) scale(1);
        }
        .nav-link-hover:hover,
        .nav-link-hover:active {
          transform: none;
          box-shadow: none;
        }
      ` : ""}
      ${navLinkHoverEffect === "border-bottom" ? `
        .nav-link-hover {
          border-bottom: 2px solid transparent;
        }
        .nav-link-hover:hover,
        .nav-link-hover:active {
          border-bottom-color: hsl(var(--primary));
          transform: none;
          box-shadow: none;
        }
      ` : ""}
      ${navLinkHoverEffect === "text-glow" ? `
        .nav-link-hover:hover,
        .nav-link-hover:active {
          text-shadow: 0 0 8px hsl(var(--primary) / 0.6), 0 0 16px hsl(var(--primary) / 0.4);
          color: hsl(var(--primary));
          transform: none;
          box-shadow: none;
        }
      ` : ""}
      ${navLinkHoverEffect === "lift" ? `
        .nav-link-hover:hover,
        .nav-link-hover:active {
          transform: translateY(-2px);
          box-shadow: none;
        }
      ` : ""}

    `;

    return () => {
      // App-level component rarely unmounts, but keep cleanup for correctness.
      styleEl?.remove();
      document.getElementById("dynamic-fonts")?.remove();
    };
  }, [themeSettings, isLoading]);

  return null;
}
